// Pieces shared by the Content screen and the 1.21 approval card: fact-check status (1.3), sources (1.1),
// AI-search checklist (1.8), voice flags and visuals (1.5).
import type { Crm } from "../lib/crm";
import type { ContentAsset, ContentItem, ContentMeta, ContentSource, FactCheck } from "../lib/types";
import { useAsync } from "../lib/useAsync";
import { fmtDate, webUrl } from "../lib/format";

export const isArticle = (c: Pick<ContentItem, "kind">) => c.kind === "pillar" || c.kind === "blog";

export function factCheckLabel(fc: FactCheck | undefined): { text: string; tone: "ok" | "bad" | "warn" | "plain" } {
  if (!fc || !fc.status) return { text: "Not fact-checked yet", tone: "plain" };
  if (fc.status === "passed") return { text: "Fact-checked: every claim has a source", tone: "ok" };
  if (fc.status === "stale") return { text: "Changed since its fact-check", tone: "warn" };
  const parts = [fc.unsupported ? `${fc.unsupported} unsupported` : "", fc.uncited ? `${fc.uncited} without a source` : ""].filter(Boolean);
  return { text: `Fact-check failed${parts.length ? `: ${parts.join(", ")}` : ""}`, tone: "bad" };
}

export function FactCheckSummary({ fc, sources }: { fc: FactCheck | undefined; sources?: ContentSource[] }) {
  const label = factCheckLabel(fc);
  const ids = new Set((sources ?? []).map((s) => s.id));
  const flagged = (fc?.claims ?? []).filter((c) => !c.supported || c.sources.length === 0 || c.sources.some((s) => !ids.has(s)));
  const uncited = fc?.notes?.uncited_sentences ?? [];
  return (
    <div className="fact-check">
      <span className={`chip chip-${label.tone}`}>{label.text}</span>
      {fc?.claims && fc.claims.length > 0 && (
        <span className="muted small"> {fc.claims.length} claims checked{fc.checked_at ? ` on ${fmtDate(fc.checked_at)}` : ""}.</span>
      )}
      {(flagged.length > 0 || uncited.length > 0) && (
        <div className="flagged">
          <p className="small">
            <strong>To resolve before it can be published:</strong>
          </p>
          <ul className="small">
            {flagged.map((c, i) => (
              <li key={`c${i}`}>
                {c.claim}
                <span className="muted">
                  {" "}
                  ({c.sources.length ? `cites ${c.sources.join(", ")}` : "no source"}
                  {c.note ? `: ${c.note}` : ""})
                </span>
              </li>
            ))}
            {uncited.map((s, i) => (
              <li key={`u${i}`}>
                {s} <span className="muted">(no source)</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function SourceList({ sources }: { sources: ContentSource[] | undefined }) {
  if (!sources?.length) return <p className="muted small">No sources recorded.</p>;
  return (
    <ol className="sources small">
      {sources.map((s) => (
        <li key={s.id}>
          <strong>[{s.id}]</strong>{" "}
          {webUrl(s.url) ? (
            <a href={webUrl(s.url)!} target="_blank" rel="noreferrer noopener">
              {s.title || s.url}
            </a>
          ) : (
            s.title || s.url
          )}
          {s.publisher ? `, ${s.publisher}` : ""}
          {s.published_at ? `, ${fmtDate(s.published_at)}` : ""}
        </li>
      ))}
    </ol>
  );
}

const CHECK_LABELS: Record<string, string> = {
  summary: "Short summary",
  question_headings: "Question-led headings",
  direct_answer_first: "Direct answer first",
  named_sources: "Named sources",
  faq: "Questions and answers",
};

export function AiSearchChecklist({ meta }: { meta: ContentMeta | undefined }) {
  const checks = meta?.ai_search?.checks;
  if (!checks) return null;
  return (
    <ul className="checklist small" aria-label="Structure for AI search">
      {Object.entries(checks).map(([k, ok]) => (
        <li key={k} className={ok ? "ok" : "missing"}>
          {CHECK_LABELS[k] ?? k}: {ok ? "done" : "missing"}
        </li>
      ))}
    </ul>
  );
}

export function VoiceFlags({ meta }: { meta: ContentMeta | undefined }) {
  const flags = [...(meta?.voice_flags ?? []), ...(meta?.warnings ?? [])];
  if (!flags.length) return null;
  return <p className="small warn-text">Check: {flags.join("; ")}.</p>;
}

function Visual({ crm, asset }: { crm: Crm; asset: ContentAsset }) {
  const url = useAsync(() => crm.assetUrl(asset), [crm, asset.storage_path, asset.svg]);
  if (url.loading || url.error || !url.data) return <span className="visual visual-missing">{asset.alt || asset.name}</span>;
  return <img className="visual" src={url.data} alt={asset.alt || `Visual: ${asset.name}`} loading="lazy" />;
}

export function VisualStrip({ crm, assets }: { crm: Crm; assets: ContentAsset[] | undefined }) {
  if (!assets?.length) return null;
  return (
    <div className="visuals" aria-label="Visuals">
      {assets.map((a) => (
        <Visual key={a.name} crm={crm} asset={a} />
      ))}
    </div>
  );
}
